/**
 * Shared stepper form field styles — keep every step looking the same.
 * Field names / Formik logic stay in each step; only visual tokens live here.
 */

export const STEPPER_PRIMARY = "#22419e";
export const STEPPER_BORDER = "#dce3ef";
export const STEPPER_BORDER_HOVER = "#b8c4d9";
export const STEPPER_MUTED = "#64748b";
export const STEPPER_LABEL = "#475569";

/** Label above each field */
export const stepperLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  mb: 0.75,
  color: STEPPER_LABEL,
  letterSpacing: "0.01em",
  lineHeight: 1.35,
};

/** Section title inside a step */
export const stepperSectionTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#0f172a",
  letterSpacing: "-0.02em",
};

/** Standard single-line TextField / Select / Autocomplete input */
export const stepperTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "11px",
    minHeight: "44px",
    height: "44px",
    backgroundColor: "#ffffff",
    fontSize: "14px",
    transition: "box-shadow 0.15s ease, background-color 0.15s ease",
    "& fieldset": {
      borderColor: STEPPER_BORDER,
      transition: "border-color 0.15s ease",
    },
    "&:hover fieldset": {
      borderColor: STEPPER_BORDER_HOVER,
    },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 3px rgba(34, 65, 158, 0.12)",
    },
    "&.Mui-focused fieldset": {
      borderColor: STEPPER_PRIMARY,
      borderWidth: "1px",
    },
    "&.Mui-error fieldset": {
      borderColor: "#dc2626",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f1f5f9",
    },
  },
  "& .MuiInputBase-input": {
    py: "10px",
    px: "14px",
    fontSize: "14px",
  },
  "& .MuiFormHelperText-root": {
    mx: "2px",
    mt: "6px",
    fontSize: "12px",
  },
};

/** Multiline TextField */
export const stepperTextareaSx = {
  ...stepperTextFieldSx,
  "& .MuiOutlinedInput-root": {
    ...stepperTextFieldSx["& .MuiOutlinedInput-root"],
    height: "auto",
    minHeight: "88px",
    alignItems: "flex-start",
    py: 0.5,
  },
};

/** Autocomplete renderInput TextField (same look, allow adornments) */
export const stepperAutocompleteInputSx = {
  ...stepperTextFieldSx,
  "& .MuiOutlinedInput-root": {
    ...stepperTextFieldSx["& .MuiOutlinedInput-root"],
    paddingRight: "9px",
  },
};

/** Parent wrapper — forces remaining MUI inputs in the step to match */
export const stepperFormScopeSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "11px !important",
    backgroundColor: "#ffffff",
    fontSize: "14px",
  },
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": {
    minHeight: "44px",
    height: "44px",
  },
  "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
    minHeight: "88px",
    height: "auto",
    alignItems: "flex-start",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: STEPPER_BORDER,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: STEPPER_BORDER_HOVER,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${STEPPER_PRIMARY} !important`,
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: "0 0 0 3px rgba(34, 65, 158, 0.12)",
  },
  "& .MuiFormHelperText-root": {
    fontSize: "12px",
    marginLeft: "2px",
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: "44px !important",
    py: "0 !important",
  },
  "& .MuiAutocomplete-inputRoot": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
  },
};
